import {
  integrityPulseCssClass as heartPulseClass,
  integrityPulsePeriodSec,
} from "../../../shared/audio/bands";
import { createLieArt, updateLieArtEl } from "../../../shared/cards/lieArt";
import { lieArtDefId, lieArtFallbackId } from "../data/lies";
import { createPrayerArt, updatePrayerArtEl } from "../../../shared/cards/prayerArt";
import type { BoardState } from "../game/board/board";
import {
  ADVANCE_INTENT_IMMINENT,
  collectAdvanceIntents,
  type AdvanceIntent,
} from "../game/board/advanceIntent";
import {
  TAX_INTENT_IMMINENT,
  collectTaxIntents,
  type TaxIntent,
} from "../game/board/taxIntent";
import {
  MULTIPLY_INTENT_IMMINENT,
  canShowMultiplyWindup,
  collectMultiplyIntents,
  multiplyProgress,
  type MultiplyIntent,
} from "../game/board/multiplyIntent";
import {
  collectContestLinks,
  type BoardFxEvent,
  type StrikeQuality,
} from "../game/boardFx";
import {
  DEFAULT_COMBAT_FEEDBACK,
  effectiveMotion,
  isCriticalBoardFx,
  motionFeatures,
  type CombatFeedbackSettings,
  type MotionFeatures,
  type MotionIntensity,
} from "../game/combatFeedback";
import { LAYER_ORDER } from "../game/board/types";
import type { LayerId } from "../game/board/types";
import { tiltBand } from "../game/board/tiltBands";
import type { DragAimMode } from "../game/cards/dragAim";
import type { LegalTargetPlan } from "../game/cards/legalTargets";
import { slotKey } from "../game/cards/legalTargets";
import {
  DOGMA_SHELL_HP,
  dogmaDemandLabel,
  dogmaIsCore,
  dogmaShellIsGrinding,
  dogmaVestmentPips,
  isDogmaPresence,
} from "../game/combat/dogma";
import {
  doubtCoreExposed,
  doubtFaithProgress,
} from "../game/combat/doubt";
import type { DragIntent, Presence } from "../game/cards/types";
import { levelRoman } from "../game/cards/types";

/**
 * Concentric onion for Doubt — fixed three rings (outer / mid / core).
 * - Active Doubt shell: lit (teal)
 * - Peeled to Faith: same ring, dimmed/disabled
 * - Not yet grown (tangle room): faint border only
 * Core: sealed until all current outers are Faith, then open (gold).
 */
function applyDoubtOnionState(
  outer: HTMLElement,
  mid: HTMLElement,
  core: HTMLElement,
  d: NonNullable<Presence["doubt"]>,
): void {
  const maxO = Math.max(0, Math.min(2, d.maxOuterCount));
  const count = Math.max(0, Math.min(maxO, d.outerCount));
  const faith = Math.max(0, Math.min(count, d.outerFaith));
  const coreOpen = doubtCoreExposed(d);

  /**
   * Outermost ring = shell slot 0; mid = shell slot 1.
   * Peeling fills Faith from the outside in (outer first).
   */
  const shellState = (
    slot: 0 | 1,
  ): "potential" | "doubt" | "peeled" => {
    const need = slot + 1; // slot 0 needs count>=1, slot 1 needs count>=2
    if (maxO < need) return "potential"; // no capacity (rare)
    if (count < need) return "potential"; // can still tangle into this ring
    // Shell exists: peeled if faith covers this outermost-first index
    // shell 0 peeled when faith >= 1; shell 1 when faith >= 2
    if (faith >= need) return "peeled";
    return "doubt";
  };

  const setRing = (
    el: HTMLElement,
    state: "potential" | "doubt" | "peeled" | "core-sealed" | "core-open",
  ) => {
    el.classList.remove(
      "is-absent",
      "is-potential",
      "is-doubt",
      "is-faith",
      "is-peeled",
      "is-core-sealed",
      "is-core-open",
    );
    el.classList.add(`is-${state}`);
  };

  setRing(outer, shellState(0));
  setRing(mid, shellState(1));
  setRing(core, coreOpen ? "core-open" : "core-sealed");
}

function buildDoubtOnion(d: NonNullable<Presence["doubt"]>): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "doubt-onion";
  wrap.setAttribute("aria-hidden", "true");
  const outer = document.createElement("span");
  outer.className = "onion-ring onion-outer";
  const mid = document.createElement("span");
  mid.className = "onion-ring onion-mid";
  const core = document.createElement("span");
  core.className = "onion-ring onion-core";
  applyDoubtOnionState(outer, mid, core, d);
  wrap.append(outer, mid, core);
  return wrap;
}

function fillVestmentWrap(
  wrap: HTMLElement,
  d: NonNullable<Presence["dogma"]>,
): void {
  wrap.replaceChildren();
  const row = document.createElement("div");
  row.className = "vestment-pips";
  for (const pip of dogmaVestmentPips(d)) {
    const el = document.createElement("span");
    el.className = `vestment-pip is-${pip.kind} demand-${pip.demand}`;
    row.appendChild(el);
  }
  wrap.appendChild(row);
  if (dogmaShellIsGrinding(d)) {
    const grind = document.createElement("div");
    grind.className = "vestment-grind";
    const hp = Math.max(0, d.shellHp ?? DOGMA_SHELL_HP);
    for (let i = 1; i <= DOGMA_SHELL_HP; i++) {
      const el = document.createElement("span");
      el.className =
        "vestment-hp" + (i <= hp ? " is-filled" : " is-empty");
      grind.appendChild(el);
    }
    wrap.appendChild(grind);
  }
}

function buildVestmentPips(d: NonNullable<Presence["dogma"]>): HTMLElement {
  const wrap = document.createElement("div");
  wrap.className = "vestment-stack";
  wrap.setAttribute("aria-hidden", "true");
  fillVestmentWrap(wrap, d);
  return wrap;
}

function applyVestmentPips(
  wrap: HTMLElement,
  d: NonNullable<Presence["dogma"]>,
): void {
  fillVestmentWrap(wrap, d);
}

function vestmentTitle(d: NonNullable<Presence["dogma"]>): string {
  const left = d.sequence.length - d.index;
  const demand = dogmaDemandLabel(d);
  const grind = dogmaShellIsGrinding(d)
    ? ` · off-type ${d.shellHp}/${DOGMA_SHELL_HP}`
    : "";
  if (d.named || dogmaIsCore(d)) {
    return `Settled · ${left} left · wants ${demand}${grind}`;
  }
  return `Settled · ${left} left · demand unnamed${grind}`;
}

function dogmaFaceMeta(p: Presence): string {
  if (!p.dogma) return "settled";
  const left = p.dogma.sequence.length - p.dogma.index;
  const grind = dogmaShellIsGrinding(p.dogma)
    ? ` · ${p.dogma.shellHp}/${DOGMA_SHELL_HP}`
    : "";
  if (p.dogma.named || dogmaIsCore(p.dogma)) {
    return `Wants ${dogmaDemandLabel(p.dogma)} · ${left} left${grind}`;
  }
  return `? · ${left} left${grind}`;
}

function doubtOnionTitle(d: NonNullable<Presence["doubt"]>): string {
  const { faith, total } = doubtFaithProgress(d);
  if (d.outerCount === 0) {
    return "Doubt · core only · Faith Answer or Redeem → Prayer";
  }
  const open = doubtCoreExposed(d);
  return (
    `Doubt · ${faith}/${total} Faith (lit = active, dim = peeled)` +
    (open
      ? " · core open → Redeem to Prayer"
      : " · peel lit rings first")
  );
}

/** Ephemeral board flash driven by Game boardFx (not sim state). */
type LiveFx = {
  id: number;
  event: BoardFxEvent;
  born: number;
  lifeMs: number;
};

/** Soft signals for layer emblems (speech / integrity). Secret Place stays untouched. */
export type LayerVitals = {
  proclaiming: boolean;
  integrity: number;
  maxIntegrity: number;
};

const LEAN_CLASSES = [
  "lean-truth-strong",
  "lean-truth",
  "lean-contested",
  "lean-lie",
  "lean-lie-strong",
] as const;

const PULSE_CLASSES = [
  "pulse-rest",
  "pulse-alert",
  "pulse-urgent",
  "pulse-critical",
] as const;

function tiltLabel(tilt: number, layer?: LayerId): string {
  const strong = layer ? tiltBand(layer).lieStrong : 40;
  const truth = layer ? -tiltBand(layer).lieStrong : -40;
  if (tilt <= truth) return "Truth strong";
  if (tilt < -8) return "Truth lean";
  if (tilt <= 8) return "Contested";
  if (tilt < strong) return "Lie lean";
  return "Lie strong";
}

function layerLeanClass(tilt: number, layer: LayerId): string {
  const strong = tiltBand(layer).lieStrong;
  if (tilt <= -strong) return "lean-truth-strong";
  if (tilt < -8) return "lean-truth";
  if (tilt <= 8) return "lean-contested";
  if (tilt < strong) return "lean-lie";
  return "lean-lie-strong";
}

function tiltToPercent(tilt: number): number {
  return Math.max(0, Math.min(100, (tilt + 100) / 2));
}

function integrityFrac(vitals: LayerVitals): number {
  const max = Math.max(1, vitals.maxIntegrity);
  return Math.max(0, Math.min(1, vitals.integrity / max));
}

type RectMap = Map<string, DOMRect>;

export class BoardUi {
  private prevRects: RectMap = new Map();
  /** Live drag state — not closed over render args (fixes wiggle-to-highlight). */
  private dragActive = false;
  private dragAim: DragAimMode = "slot";
  /** Fallback when dataTransfer is empty (common after re-renders). */
  private dragCardId: string | null = null;
  /** Dedupes highlight updates (slot id, layer id, or "global") */
  private highlightKey: string | null = null;
  /**
   * Last presence under the cursor while dragging (instance id).
   * Used so a unit that moves mid-drag is still the drop target.
   */
  private hoverPresenceId: string | null = null;
  /** Which side sticky-aim prefers (restore Keep → player; Answers → enemy). */
  private dragPresenceSide: "enemy" | "player" = "enemy";
  /** Legal wash plan for the card currently dragged. */
  private legalPlan: LegalTargetPlan | null = null;
  private legalSlotSet = new Set<string>();
  private legalLayerSet = new Set<LayerId>();
  /** Last vitals applied (avoid redundant class thrash). */
  private lastProclaiming: boolean | null = null;
  private lastPulseClass: string | null = null;
  private lastIntegrityFrac = -1;
  private lastLean: Partial<Record<LayerId, string>> = {};
  /** SVG overlay for advance-intent arrows (recreated on full board render). */
  private motionSvg: SVGSVGElement | null = null;
  private motionLayer: SVGGElement | null = null;
  /** Discrete action FX queue (strikes, silence, redeem, …). */
  private liveFx: LiveFx[] = [];
  private fxSeq = 0;
  /** DOM bursts attached to slots (survive one full board rebuild). */
  private slotBurstQueue: Array<{
    layer: LayerId;
    slot: number;
    className: string;
    until: number;
  }> = [];
  /** Effective motion features from combat feedback settings + OS. */
  private motion: MotionFeatures = motionFeatures(
    effectiveMotion(DEFAULT_COMBAT_FEEDBACK),
  );
  private motionIntensity: MotionIntensity = effectiveMotion(
    DEFAULT_COMBAT_FEEDBACK,
  );

  constructor(
    private readonly root: HTMLElement,
    private readonly handlers: {
      onDrop: (intent: DragIntent, cardInstanceId: string) => void;
      onInspectEnemy: (presence: Presence, layer: LayerId, slot: number) => void;
      onHoverEnemy: (
        presence: Presence | null,
        layer: LayerId | null,
        slot: number | null,
        clientX: number,
        clientY: number,
      ) => void;
    },
  ) {
    // Board-level drag tracking so highlight works even if slot listeners were
    // attached before drag started (and children don't steal dragleave).
    this.root.addEventListener("dragover", (e) => {
      if (!this.dragActive) return;
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
      this.highlightFromPoint(e.clientX, e.clientY);
    });
    this.root.addEventListener("drop", (e) => {
      if (!this.dragActive) return;
      e.preventDefault();
      e.stopPropagation();
      this.resolveDrop(e.clientX, e.clientY, e.dataTransfer);
    });
    this.root.addEventListener("dragleave", (e) => {
      // Leaving the whole board — restore full valid-target wash (not hover focus)
      if (!this.root.contains(e.relatedTarget as Node)) {
        if (this.dragActive) this.applyValidTargetsHighlight(this.dragAim);
      }
    });
  }

  setDragActive(
    active: boolean,
    aim: DragAimMode = "slot",
    cardId: string | null = null,
    opts?: {
      presenceSide?: "enemy" | "player";
      legal?: LegalTargetPlan | null;
    },
  ): void {
    this.dragActive = active;
    this.dragAim = aim;
    // Keep card id briefly after drag ends so late HTML5 `drop` still resolves
    if (active) {
      this.dragCardId = cardId;
    } else if (cardId) {
      this.dragCardId = cardId;
    } else {
      // Defer clear so drop-after-dragend still finds the id
      const keepId = this.dragCardId;
      window.setTimeout(() => {
        if (!this.dragActive && this.dragCardId === keepId) {
          this.dragCardId = null;
        }
      }, 50);
    }
    this.dragPresenceSide = opts?.presenceSide ?? "enemy";
    this.legalPlan = active ? (opts?.legal ?? null) : null;
    this.legalSlotSet = new Set(this.legalPlan?.slotKeys ?? []);
    this.legalLayerSet = new Set(this.legalPlan?.layerKeys ?? []);
    if (!active) {
      this.hoverPresenceId = null;
      this.clearHighlight();
      this.root.classList.remove(
        "board-drag-active",
        "board-targeting",
        "drag-aim-slot",
        "drag-aim-layer",
        "drag-aim-global",
        "drag-wash-place",
        "drag-wash-enemies",
        "drag-wash-allies",
        "drag-wash-layers",
        "drag-wash-global",
        "drag-wash-convert",
        "drag-wash-self",
      );
      delete this.root.dataset.dragAim;
      delete this.root.dataset.dragWash;
      return;
    }
    this.hoverPresenceId = null;
    const wash = this.legalPlan?.wash ?? "enemies";
    this.root.classList.add(
      "board-drag-active",
      "board-targeting",
      `drag-aim-${aim}`,
      `drag-wash-${wash}`,
    );
    this.root.dataset.dragAim = aim;
    this.root.dataset.dragWash = wash;
    // Highlight all valid targets as soon as the drag starts
    this.applyValidTargetsHighlight();
  }

  /**
   * Wash every legal target; hover adds focus without clearing the wash.
   */
  private applyValidTargetsHighlight(focusKey: string | null = null): void {
    this.clearHighlightClasses();
    this.root.classList.add("board-targeting");

    const plan = this.legalPlan;
    if (!plan) {
      // Fallback: old aim-based wash
      this.applyLegacyWash(focusKey);
      return;
    }

    if (plan.wash === "self" || plan.wash === "global") {
      this.root.classList.add("drop-target-global");
    }

    // Legal layers (soft row glow)
    for (const L of plan.layerKeys) {
      const row = this.root.querySelector(`.layer[data-layer="${L}"]`);
      row?.classList.add("drop-target-layer", "drop-valid-layer");
    }

    // Legal slots
    this.root.querySelectorAll<HTMLElement>(".slot").forEach((slot) => {
      const layer = slot.dataset.layer as LayerId | undefined;
      const si = slot.dataset.slot;
      if (!layer || si === undefined) return;
      const key = slotKey(layer, Number(si));
      if (!this.legalSlotSet.has(key)) {
        slot.classList.add("drop-invalid");
        return;
      }
      slot.classList.add("drop-target", "drop-valid");
      if (slot.classList.contains("is-empty") && plan.emptyHint) {
        this.ensureDropBadge(slot, plan.emptyHint);
      }
      const presence = slot.querySelector(
        this.presenceSideSelector(),
      ) as HTMLElement | null;
      if (presence) {
        presence.classList.add("drop-aim");
        if (plan.presenceHint) {
          this.ensureDropBadge(presence, plan.presenceHint);
        }
      }
    });

    // Dim layers with no legal slots (except self/global where whole board is OK)
    if (plan.wash !== "self" && plan.wash !== "global") {
      this.root.querySelectorAll<HTMLElement>(".layer").forEach((row) => {
        const L = row.dataset.layer as LayerId | undefined;
        if (!L) return;
        if (!this.legalLayerSet.has(L)) {
          row.classList.add("drop-invalid-layer");
        }
      });
    }

    if (focusKey) this.applyFocusKey(focusKey);
    this.highlightKey = focusKey ?? `wash:${plan.wash}:${plan.slotKeys.length}`;
  }

  private applyLegacyWash(focusKey: string | null): void {
    if (this.dragAim === "global") {
      this.root.classList.add("drop-target-global");
      this.root.querySelectorAll(".layer").forEach((L) => {
        L.classList.add("drop-target-layer");
      });
      this.root.querySelectorAll(".slot").forEach((s) => {
        s.classList.add("drop-target", "drop-valid");
      });
      this.root.querySelectorAll(this.presenceSideSelector()).forEach((p) => {
        p.classList.add("drop-aim");
      });
    } else if (this.dragAim === "layer") {
      this.root.querySelectorAll(".layer").forEach((L) => {
        L.classList.add("drop-target-layer", "drop-valid-layer");
      });
      this.root.querySelectorAll(".slot").forEach((s) => {
        s.classList.add("drop-target", "drop-valid");
      });
    } else {
      this.root.querySelectorAll(this.presenceSideSelector()).forEach((p) => {
        p.classList.add("drop-aim");
        p.closest(".slot")?.classList.add("drop-target", "drop-valid");
      });
    }
    if (focusKey) this.applyFocusKey(focusKey);
    this.highlightKey = focusKey ?? "legacy-wash";
  }

  private ensureDropBadge(host: HTMLElement, text: string): void {
    let badge = host.querySelector(":scope > .drop-badge") as HTMLElement | null;
    if (!badge) {
      badge = document.createElement("span");
      badge.className = "drop-badge";
      badge.setAttribute("aria-hidden", "true");
      host.appendChild(badge);
    }
    badge.textContent = text;
  }

  private applyFocusKey(focusKey: string): void {
    if (focusKey.startsWith("layer:")) {
      const layerId = focusKey.split(":")[1];
      const row = this.root.querySelector(`.layer[data-layer="${layerId}"]`);
      row?.classList.add("drop-focus-layer");
      row?.querySelectorAll(".slot.drop-valid").forEach((s) => {
        s.classList.add("drop-focus");
      });
      return;
    }
    if (focusKey.startsWith("presence:")) {
      const id = focusKey.slice("presence:".length);
      const p = this.root.querySelector(
        `.presence[data-id="${id.replace(/"/g, "")}"]`,
      ) as HTMLElement | null;
      if (p) {
        p.classList.add("drop-aim-focus");
        p.closest(".slot")?.classList.add("drop-focus");
      }
      return;
    }
    if (focusKey.startsWith("slot:")) {
      const [, layer, slot] = focusKey.split(":");
      const el = this.root.querySelector(
        `.slot[data-layer="${layer}"][data-slot="${slot}"]`,
      );
      el?.classList.add("drop-focus");
    }
  }

  /** Update drop-target highlight while dragging (mouse, touch, or pen). */
  updateDragAt(clientX: number, clientY: number): void {
    if (!this.dragActive) return;
    this.highlightFromPoint(clientX, clientY);
  }

  /** Resolve a drop at client coordinates (board or stage fallback). */
  resolveDrop(
    clientX: number,
    clientY: number,
    dataTransfer: DataTransfer | null = null,
  ): boolean {
    // HTML5 DnD often fires dragend before drop — accept card id from
    // dataTransfer or a briefly retained dragCardId even if dragActive is off.
    const cardId =
      dataTransfer?.getData("text/card-id") ||
      dataTransfer?.getData("text/plain") ||
      this.dragCardId ||
      "";
    if (!cardId) return false;

    // Prefer the enemy under the pointer (DOM may still show pre-move layout during drag)
    const presenceEl = this.presenceFromPoint(clientX, clientY);
    const presenceId =
      presenceEl?.dataset.id || this.hoverPresenceId || undefined;

    let slotEl = this.slotFromPoint(clientX, clientY);
    // If we hit a presence that's no longer in this slot in game state, still
    // pass its visual slot for fallbacks; Game resolves by presenceId first.
    if (!slotEl && presenceEl) {
      slotEl = presenceEl.closest?.(".slot") as HTMLElement | null;
    }

    let layer: LayerId | undefined;
    let slot = 1;

    if (slotEl && this.root.contains(slotEl)) {
      layer = slotEl.dataset.layer as LayerId | undefined;
      slot = Number(slotEl.dataset.slot);
    } else {
      // Fallback: drop on layer row (or any board point for global)
      const layerEl = this.layerFromPoint(clientX, clientY);
      if (layerEl && this.root.contains(layerEl)) {
        layer = layerEl.dataset.layer as LayerId | undefined;
        slot = 1;
      } else if (
        this.dragAim === "global" &&
        this.root.contains(
          document.elementFromPoint(clientX, clientY) as Node,
        )
      ) {
        layer = "will";
        slot = 1;
      }
    }

    // Sticky presence alone is enough (lie may have vacated the visual slot)
    if (!layer && presenceId) {
      layer = "mind";
      slot = 0;
    }

    this.clearHighlight();
    this.hoverPresenceId = null;
    if (!layer || Number.isNaN(slot)) return false;
    this.handlers.onDrop(
      { layer, slot, presenceId },
      cardId,
    );
    return true;
  }

  private presenceSideSelector(): string {
    return this.dragPresenceSide === "player"
      ? ".presence.side-player"
      : ".presence.side-enemy";
  }

  private presenceFromPoint(x: number, y: number): HTMLElement | null {
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    const sel = this.presenceSideSelector();
    const p = (el as HTMLElement).closest?.(sel) as HTMLElement | null;
    if (p && this.root.contains(p)) return p;
    // Slot under point may still hold the visual presence during deferred rebuild
    const slot = (el as HTMLElement).closest?.(".slot") as HTMLElement | null;
    if (slot && this.root.contains(slot)) {
      return slot.querySelector(sel) as HTMLElement | null;
    }
    return null;
  }

  private slotFromPoint(x: number, y: number): HTMLElement | null {
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    return (el as HTMLElement).closest?.(".slot") as HTMLElement | null;
  }

  private layerFromPoint(x: number, y: number): HTMLElement | null {
    const el = document.elementFromPoint(x, y);
    if (!el) return null;
    return (el as HTMLElement).closest?.(".layer") as HTMLElement | null;
  }

  private highlightFromPoint(x: number, y: number): void {
    // Always remember the presence under the pointer (sticky aim)
    const under = this.presenceFromPoint(x, y);
    if (under?.dataset.id) this.hoverPresenceId = under.dataset.id;

    let focusKey: string | null = null;

    if (this.dragAim === "layer" || this.legalPlan?.wash === "layers") {
      const layerEl =
        this.layerFromPoint(x, y) ??
        this.slotFromPoint(x, y)?.closest?.(".layer") ??
        null;
      const layerId = (layerEl as HTMLElement | null)?.dataset.layer as
        | LayerId
        | undefined;
      if (
        layerId &&
        (!this.legalLayerSet.size || this.legalLayerSet.has(layerId))
      ) {
        focusKey = `layer:${layerId}`;
      }
    } else if (under?.dataset.id) {
      const host = under.closest(".slot") as HTMLElement | null;
      const layer = host?.dataset.layer as LayerId | undefined;
      const si = host?.dataset.slot;
      const key =
        layer !== undefined && si !== undefined
          ? slotKey(layer, Number(si))
          : null;
      if (!key || !this.legalSlotSet.size || this.legalSlotSet.has(key)) {
        focusKey = `presence:${under.dataset.id}`;
      }
    } else {
      const slot = this.slotFromPoint(x, y);
      if (slot && this.root.contains(slot)) {
        const layer = slot.dataset.layer as LayerId | undefined;
        const si = slot.dataset.slot;
        if (layer !== undefined && si !== undefined) {
          const key = slotKey(layer, Number(si));
          if (!this.legalSlotSet.size || this.legalSlotSet.has(key)) {
            focusKey = `slot:${layer}:${si}`;
          }
        }
      }
    }

    // Always re-apply full wash + optional focus (do not clear valid targets)
    const washKey = `wash+${focusKey ?? "none"}`;
    if (washKey === this.highlightKey) return;
    this.applyValidTargetsHighlight(focusKey);
  }

  private clearHighlightClasses(): void {
    this.root.classList.remove("drop-target-global");
    this.root
      .querySelectorAll(
        ".drop-target-layer, .drop-valid-layer, .drop-invalid-layer, .drop-focus-layer",
      )
      .forEach((n) => {
        n.classList.remove(
          "drop-target-layer",
          "drop-valid-layer",
          "drop-invalid-layer",
          "drop-focus-layer",
        );
      });
    this.root
      .querySelectorAll(
        ".drop-target, .drop-valid, .drop-invalid, .drop-focus",
      )
      .forEach((n) => {
        n.classList.remove(
          "drop-target",
          "drop-valid",
          "drop-invalid",
          "drop-focus",
        );
      });
    this.root.querySelectorAll(".drop-aim, .drop-aim-focus").forEach((n) => {
      n.classList.remove("drop-aim", "drop-aim-focus");
    });
    this.root.querySelectorAll(".drop-badge").forEach((n) => n.remove());
  }

  private clearHighlight(): void {
    this.highlightKey = null;
    this.clearHighlightClasses();
    this.root.classList.remove("board-targeting");
  }

  render(
    board: BoardState,
    _dragCardId: string | null,
    vitals: LayerVitals = {
      proclaiming: false,
      integrity: 100,
      maxIntegrity: 100,
    },
  ): void {
    // Capture positions for FLIP move animation (structure changes only)
    const first: RectMap = new Map();
    this.root.querySelectorAll<HTMLElement>(".presence[data-id]").forEach((el) => {
      const id = el.dataset.id;
      if (id) first.set(id, el.getBoundingClientRect());
    });

    // Reset lean cache — rows are rebuilt
    this.lastLean = {};
    this.lastProclaiming = null;
    this.lastPulseClass = null;
    this.lastIntegrityFrac = -1;

    this.root.innerHTML = "";
    this.motionSvg = null;
    this.motionLayer = null;
    this.ensureMotionOverlay();
    for (const id of LAYER_ORDER) {
      const layer = board[id];
      const lean = layerLeanClass(layer.tilt, layer.id);
      const contested = this.layerIsContested(layer.slots);
      const row = document.createElement("section");
      row.className =
        `layer layer-${id} ${lean}` +
        (layer.flash > 0 ? " flashing" : "") +
        (contested ? " is-contested" : "");
      row.dataset.layer = id;
      this.lastLean[id] = lean;

      const header = document.createElement("div");
      header.className = "layer-header";

      const title = document.createElement("div");
      title.className = "layer-title";

      const emblem = this.buildEmblem(id);
      const name = document.createElement("span");
      name.className = "layer-name";
      name.textContent = layer.label;
      title.append(emblem, name);

      const meter = document.createElement("div");
      meter.className = "layer-meter";
      meter.title = "Truth ← → Lie";

      const track = document.createElement("div");
      track.className = "meter-track";
      const fillTruth = document.createElement("div");
      fillTruth.className = "meter-fill meter-fill-truth";
      const fillLie = document.createElement("div");
      fillLie.className = "meter-fill meter-fill-lie";
      const center = document.createElement("div");
      center.className = "meter-center";
      const needle = document.createElement("div");
      needle.className = "meter-needle";
      this.applyMeterVisual(track, fillTruth, fillLie, needle, layer.tilt);
      track.append(fillTruth, fillLie, center, needle);

      const mLabel = document.createElement("span");
      mLabel.className = "meter-label";
      mLabel.textContent = tiltLabel(layer.tilt, layer.id);

      meter.append(track, mLabel);
      header.append(title, meter);

      const slots = document.createElement("div");
      slots.className = "slots";

      layer.slots.forEach((presence, slotIndex) => {
        const slot = document.createElement("div");
        slot.className = "slot";
        slot.dataset.layer = id;
        slot.dataset.slot = String(slotIndex);

        if (!presence) {
          // Soft floor plate only — no "Empty" label (calendar smell).
          slot.classList.add("is-empty");
          slot.setAttribute("aria-label", "Open ground");
          const floor = document.createElement("span");
          floor.className = "slot-floor";
          floor.setAttribute("aria-hidden", "true");
          slot.appendChild(floor);
        } else {
          slot.appendChild(
            this.buildPresenceEl(presence, id as LayerId, slotIndex),
          );
        }

        slots.appendChild(slot);
      });

      row.append(header, slots);
      this.root.appendChild(row);
    }

    this.applyVitals(vitals);

    // Board rebuild wipes classes — restore legal wash if still dragging
    if (this.dragActive) {
      this.highlightKey = null;
      this.applyValidTargetsHighlight();
    }

    // FLIP: only when the same instance moved slots (not every soft update)
    requestAnimationFrame(() => {
      this.root.querySelectorAll<HTMLElement>(".presence[data-id]").forEach((el) => {
        const pid = el.dataset.id;
        if (!pid) return;
        const prev = first.get(pid);
        const next = el.getBoundingClientRect();
        if (prev) {
          const dx = prev.left - next.left;
          const dy = prev.top - next.top;
          if (Math.abs(dx) > 3 || Math.abs(dy) > 3) {
            el.classList.remove("pushing");
            el.style.setProperty("--push", "0");
            el.style.transition = "none";
            el.style.transform = `translate(${dx}px, ${dy}px)`;
            requestAnimationFrame(() => {
              requestAnimationFrame(() => {
                el.style.transition =
                  "transform 0.4s cubic-bezier(0.22, 1, 0.36, 1)";
                el.style.transform = "translate(0, 0)";
                el.classList.add("anim-slide");
                window.setTimeout(() => {
                  el.classList.remove("anim-slide");
                  el.style.transition = "";
                  el.style.transform = "";
                }, 420);
              });
            });
          }
        }
        this.prevRects.set(pid, next);
      });
      for (const key of [...this.prevRects.keys()]) {
        if (!this.root.querySelector(`.presence[data-id="${CSS.escape(key)}"]`)) {
          this.prevRects.delete(key);
        }
      }
      // After layout (and FLIP setup), draw advance + multiply intents
      this.updateMotionIntents(board);
    });
  }

  private layerIsContested(slots: (Presence | null)[]): boolean {
    let player = false;
    let enemy = false;
    for (const p of slots) {
      if (!p) continue;
      if (p.side === "player") player = true;
      else enemy = true;
      if (player && enemy) return true;
    }
    return false;
  }

  private buildEmblem(id: LayerId): HTMLElement {
    const wrap = document.createElement("span");
    wrap.className = `layer-emblem emblem-${id}`;
    wrap.setAttribute("aria-hidden", "true");

    if (id === "mind") {
      // Lamp / thought: ring + beam that waves while proclaiming
      wrap.innerHTML =
        '<span class="emblem-glyph mind-glyph">' +
        '<span class="mind-ring"></span>' +
        '<span class="mind-beam"></span>' +
        "</span>";
    } else if (id === "heart") {
      wrap.innerHTML =
        '<span class="emblem-glyph heart-glyph">' +
        '<span class="heart-shape"></span>' +
        "</span>";
    } else {
      // Pillar fill = integrity
      wrap.innerHTML =
        '<span class="emblem-glyph will-glyph">' +
        '<span class="will-pillar">' +
        '<span class="will-fill"></span>' +
        "</span>" +
        '<span class="will-cap"></span>' +
        "</span>";
    }
    return wrap;
  }

  private applyMeterVisual(
    track: HTMLElement,
    fillTruth: HTMLElement,
    fillLie: HTMLElement,
    needle: HTMLElement,
    tilt: number,
  ): void {
    const pct = tiltToPercent(tilt);
    const truthW = Math.max(0, -tilt) / 2; // 0–50% of track, expands left from center
    const lieW = Math.max(0, tilt) / 2;
    track.style.setProperty("--truth-w", `${truthW}%`);
    track.style.setProperty("--lie-w", `${lieW}%`);
    fillTruth.style.width = `${truthW}%`;
    fillLie.style.width = `${lieW}%`;
    needle.style.left = `${pct}%`;
  }

  private applyVitals(vitals: LayerVitals): void {
    const proclaiming = vitals.proclaiming;
    if (proclaiming !== this.lastProclaiming) {
      this.root.classList.toggle("is-proclaiming", proclaiming);
      this.lastProclaiming = proclaiming;
    }

    const frac = integrityFrac(vitals);
    const pulse = heartPulseClass(frac);
    const heartRow = this.root.querySelector(".layer-heart");
    if (heartRow && pulse !== this.lastPulseClass) {
      for (const c of PULSE_CLASSES) heartRow.classList.remove(c);
      heartRow.classList.add(pulse);
      this.lastPulseClass = pulse;
    }
    // Continuous period so visual rate eases with integrity (matches audio BPM)
    if (heartRow && Math.abs(frac - this.lastIntegrityFrac) >= 0.01) {
      const glyph = heartRow.querySelector(".heart-glyph") as HTMLElement | null;
      if (glyph) {
        glyph.style.animationDuration = `${integrityPulsePeriodSec(frac).toFixed(3)}s`;
      }
    }

    // Will fill: update when integrity moves ~1%
    if (Math.abs(frac - this.lastIntegrityFrac) >= 0.01) {
      this.root.style.setProperty("--integrity-frac", String(frac));
      const willFill = this.root.querySelector(
        ".layer-will .will-fill",
      ) as HTMLElement | null;
      if (willFill) {
        willFill.style.transform = `scaleY(${frac})`;
      }
      const willEmblem = this.root.querySelector(".layer-will .layer-emblem");
      willEmblem?.classList.toggle("will-low", frac < 0.35);
      willEmblem?.classList.toggle("will-critical", frac < 0.2);
      this.lastIntegrityFrac = frac;
    }
  }

  private buildPresenceEl(
    presence: Presence,
    layer: LayerId,
    slot: number,
  ): HTMLElement {
    const card = document.createElement("div");
    card.className = `presence side-${presence.side}`;
    card.dataset.id = presence.instanceId;
    if (presence.lieType) card.classList.add(`type-${presence.lieType}`);
    if (isDogmaPresence(presence)) card.classList.add("is-boss");
    if (presence.category) card.classList.add(`cat-${presence.category}`);
    if (presence.anim) card.classList.add(`anim-${presence.anim}`);
    if (presence.advanceProgress > 0.08 && presence.verbs.advanceAfter) {
      card.classList.add("pushing");
      if (presence.side === "player") card.classList.add("pushing-out");
      card.style.setProperty(
        "--push",
        String(Math.min(1, presence.advanceProgress)),
      );
    }
    if ((presence.taxProgress ?? 0) > 0.08 && presence.verbs.taxAfter) {
      card.classList.add("accusing");
    }
    if (presence.side === "enemy" && presence.slowTimer > 0) {
      card.classList.add("slowed");
    }
    if (presence.side === "player" && presence.fortifyTimer > 0) {
      card.classList.add("fortified");
    }

    if (presence.side === "enemy") {
      card.classList.add("inspectable");
      card.tabIndex = 0;
      card.setAttribute("role", "button");
      card.setAttribute(
        "aria-label",
        `${presence.name}. ${presence.lieType ?? "lie"}. Click to inspect.`,
      );
      card.addEventListener("click", (e) => {
        e.stopPropagation();
        this.handlers.onInspectEnemy(presence, layer, slot);
      });
      card.addEventListener("keydown", (e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          this.handlers.onInspectEnemy(presence, layer, slot);
        }
      });
      card.addEventListener("pointerenter", (e) => {
        if (this.dragActive) return;
        this.handlers.onHoverEnemy(presence, layer, slot, e.clientX, e.clientY);
      });
      card.addEventListener("pointermove", (e) => {
        if (this.dragActive) return;
        this.handlers.onHoverEnemy(presence, layer, slot, e.clientX, e.clientY);
      });
      card.addEventListener("pointerleave", () => {
        this.handlers.onHoverEnemy(null, null, null, 0, 0);
      });
    }

    // Full-bleed art; footer text overlays on top (fixed lane size).
    if (presence.side === "enemy") {
      card.appendChild(
        createLieArt({
          defId: lieArtDefId(presence),
          fallbackDefId: lieArtFallbackId(presence),
          level: presence.level,
          name: presence.name,
        }),
      );
    } else {
      card.appendChild(
        createPrayerArt({
          defId: presence.defId,
          level: presence.level,
          name: presence.name,
          category: presence.category,
        }),
      );
    }

    const foot = document.createElement("div");
    foot.className = "presence-foot";

    const n = document.createElement("span");
    n.className = "presence-name";
    n.textContent = presence.name;

    const m = document.createElement("span");
    m.className = "presence-meta";
    if (presence.side === "enemy") {
      if (isDogmaPresence(presence) && presence.dogma) {
        m.textContent = dogmaFaceMeta(presence);
        if (presence.slowTimer > 0) m.textContent += " · still";
      } else {
        const bits: string[] = [presence.lieType ?? "lie"];
        if (presence.doubt) {
          if (presence.doubt.outerCount === 0) {
            bits.push("core");
          } else {
            const total = presence.doubt.outerCount + 1;
            bits.push(`${presence.doubt.outerFaith}/${total} Faith`);
            if (presence.doubt.outerFaith >= presence.doubt.outerCount) {
              bits.push("core");
            }
          }
        } else {
          bits.push(levelRoman(presence.level));
        }
        if (presence.slowTimer > 0) bits.push("still");
        m.textContent = bits.join(" · ");
      }
    } else {
      const bits = [
        presence.category ?? "prayer",
        levelRoman(presence.level),
      ];
      if (presence.fortifyTimer > 0) bits.push("renewed");
      m.textContent = bits.join(" · ");
    }

    foot.append(n, m);

    // Enemy claims (campaign skins especially) — also on hover / inspect
    if (presence.side === "enemy" && presence.claim) {
      const claim = document.createElement("span");
      claim.className =
        "presence-claim" + (isDogmaPresence(presence) ? " presence-claim-boss" : "");
      claim.textContent = presence.claim;
      foot.appendChild(claim);
    }
    if (presence.verbs.taxAfter) {
      const taxBar = document.createElement("div");
      taxBar.className = "presence-tax";
      const taxFill = document.createElement("div");
      taxFill.className = "presence-tax-fill";
      taxFill.style.width = `${Math.round((presence.taxProgress ?? 0) * 100)}%`;
      taxBar.appendChild(taxFill);
      if ((presence.taxProgress ?? 0) < 0.08) taxBar.classList.add("is-idle");
      foot.appendChild(taxBar);
    }
    if (presence.verbs.advanceAfter || presence.marching) {
      const pushBar = document.createElement("div");
      pushBar.className =
        "presence-push" +
        (presence.side === "player" ? " presence-push-out" : "");
      const pushFill = document.createElement("div");
      pushFill.className = "presence-push-fill";
      pushFill.style.width = `${Math.round(presence.advanceProgress * 100)}%`;
      pushBar.appendChild(pushFill);
      foot.appendChild(pushBar);
    }
    if (canShowMultiplyWindup(presence)) {
      const spreadBar = document.createElement("div");
      spreadBar.className = "presence-spread";
      const spreadFill = document.createElement("div");
      spreadFill.className = "presence-spread-fill";
      const sp = multiplyProgress(presence);
      spreadFill.style.width = `${Math.round(sp * 100)}%`;
      spreadBar.appendChild(spreadFill);
      if (sp < 0.08) spreadBar.classList.add("is-idle");
      foot.appendChild(spreadBar);
      if (sp >= 0.08) card.classList.add("spreading");
    }

    const levelBar = document.createElement("div");
    levelBar.className = "presence-level";
    if (presence.side === "enemy" && presence.doubt) {
      levelBar.classList.add("presence-onion");
      levelBar.title = doubtOnionTitle(presence.doubt);
      levelBar.appendChild(buildDoubtOnion(presence.doubt));
    } else if (presence.side === "enemy" && presence.dogma) {
      levelBar.classList.add("presence-vestment");
      levelBar.title = vestmentTitle(presence.dogma);
      levelBar.appendChild(buildVestmentPips(presence.dogma));
    } else {
      levelBar.title = `Level ${levelRoman(presence.level)} · wear ${Math.round(presence.wear * 100)}%`;
      const pips = document.createElement("div");
      pips.className = "presence-level-pips";
      for (let i = 1; i <= Math.max(presence.maxLevel, 1); i++) {
        const pip = document.createElement("span");
        pip.className =
          "level-pip" + (i <= presence.level ? " filled" : " empty");
        pips.appendChild(pip);
      }
      const wear = document.createElement("div");
      wear.className = "presence-wear-fill";
      wear.style.width = `${Math.max(0, Math.min(100, presence.wear * 100))}%`;
      levelBar.append(pips, wear);
    }
    foot.appendChild(levelBar);

    card.appendChild(foot);

    return card;
  }

  updateMeters(
    board: BoardState,
    vitals: LayerVitals = {
      proclaiming: false,
      integrity: 100,
      maxIntegrity: 100,
    },
  ): void {
    for (const id of LAYER_ORDER) {
      const layer = board[id];
      const row = this.root.querySelector(`.layer-${id}`);
      if (!row) continue;
      row.classList.toggle("flashing", layer.flash > 0);
      row.classList.toggle("is-contested", this.layerIsContested(layer.slots));

      const lean = layerLeanClass(layer.tilt, layer.id);
      if (this.lastLean[id] !== lean) {
        for (const c of LEAN_CLASSES) row.classList.remove(c);
        row.classList.add(lean);
        this.lastLean[id] = lean;
      }

      const track = row.querySelector(".meter-track") as HTMLElement | null;
      const fillTruth = row.querySelector(
        ".meter-fill-truth",
      ) as HTMLElement | null;
      const fillLie = row.querySelector(".meter-fill-lie") as HTMLElement | null;
      const needle = row.querySelector(".meter-needle") as HTMLElement | null;
      const label = row.querySelector(".meter-label") as HTMLElement | null;
      if (track && fillTruth && fillLie && needle) {
        this.applyMeterVisual(track, fillTruth, fillLie, needle, layer.tilt);
      }
      if (label) label.textContent = tiltLabel(layer.tilt, layer.id);

      layer.slots.forEach((presence, slotIndex) => {
        if (!presence) return;
        const el = row.querySelector(
          `.slot[data-slot="${slotIndex}"] .presence`,
        ) as HTMLElement | null;
        if (!el) return;
        const advancing =
          presence.advanceProgress > 0.08 && !!presence.verbs.advanceAfter;
        el.classList.toggle("pushing", advancing);
        el.classList.toggle(
          "pushing-out",
          advancing && presence.side === "player",
        );
        el.classList.toggle(
          "accusing",
          (presence.taxProgress ?? 0) > 0.08 && !!presence.verbs.taxAfter,
        );
        el.classList.toggle("slowed", presence.slowTimer > 0);
        el.classList.toggle(
          "fortified",
          presence.side === "player" && presence.fortifyTimer > 0,
        );
        el.style.setProperty(
          "--push",
          String(Math.min(1, presence.advanceProgress)),
        );
        const taxFill = el.querySelector(
          ".presence-tax-fill",
        ) as HTMLElement | null;
        if (taxFill) {
          taxFill.style.width = `${Math.round((presence.taxProgress ?? 0) * 100)}%`;
          taxFill.parentElement?.classList.toggle(
            "is-idle",
            (presence.taxProgress ?? 0) < 0.08,
          );
          taxFill.parentElement?.classList.toggle(
            "is-imminent",
            (presence.taxProgress ?? 0) >= TAX_INTENT_IMMINENT,
          );
        }
        const pushFill = el.querySelector(".presence-push-fill") as HTMLElement | null;
        if (pushFill) {
          pushFill.style.width = `${Math.round(presence.advanceProgress * 100)}%`;
        }
        const spreadFill = el.querySelector(
          ".presence-spread-fill",
        ) as HTMLElement | null;
        if (spreadFill && canShowMultiplyWindup(presence)) {
          const sp = multiplyProgress(presence);
          spreadFill.style.width = `${Math.round(sp * 100)}%`;
          const spreadBar = spreadFill.parentElement;
          spreadBar?.classList.toggle("is-idle", sp < 0.08);
          el.classList.toggle("spreading", sp >= 0.08);
        } else {
          el.classList.remove("spreading");
        }
        const wearFill = el.querySelector(
          ".presence-wear-fill",
        ) as HTMLElement | null;
        if (wearFill) {
          wearFill.style.width = `${Math.max(0, Math.min(100, presence.wear * 100))}%`;
        }
        if (presence.doubt) {
          const onion = el.querySelector(".doubt-onion");
          const outer = el.querySelector(".onion-outer") as HTMLElement | null;
          const mid = el.querySelector(".onion-mid") as HTMLElement | null;
          const core = el.querySelector(".onion-core") as HTMLElement | null;
          if (onion && outer && mid && core) {
            applyDoubtOnionState(outer, mid, core, presence.doubt);
            const levelBar = el.querySelector(
              ".presence-level",
            ) as HTMLElement | null;
            if (levelBar) levelBar.title = doubtOnionTitle(presence.doubt);
          }
        } else if (presence.dogma) {
          const vest = el.querySelector(".vestment-stack") as HTMLElement | null;
          if (vest) applyVestmentPips(vest, presence.dogma);
          const levelBar = el.querySelector(
            ".presence-level",
          ) as HTMLElement | null;
          if (levelBar) levelBar.title = vestmentTitle(presence.dogma);
        } else {
          const pips = el.querySelectorAll(".level-pip");
          pips.forEach((pip, i) => {
            pip.classList.toggle("filled", i < presence.level);
            pip.classList.toggle("empty", i >= presence.level);
          });
        }
        const nameEl = el.querySelector(".presence-name");
        if (nameEl && nameEl.textContent !== presence.name) {
          nameEl.textContent = presence.name;
        }
        const claimEl = el.querySelector(".presence-claim");
        if (claimEl && presence.side === "enemy") {
          claimEl.textContent = presence.claim;
        }
        const meta = el.querySelector(".presence-meta");
        if (meta && presence.side === "enemy") {
          if (isDogmaPresence(presence) && presence.dogma) {
            let line = dogmaFaceMeta(presence);
            if (presence.slowTimer > 0) line += " · still";
            meta.textContent = line;
          } else {
            const bits: string[] = [presence.lieType ?? "lie"];
            if (presence.doubt) {
              if (presence.doubt.outerCount === 0) {
                bits.push("core");
              } else {
                const total = presence.doubt.outerCount + 1;
                bits.push(`${presence.doubt.outerFaith}/${total} Faith`);
                if (presence.doubt.outerFaith >= presence.doubt.outerCount) {
                  bits.push("core");
                }
              }
            } else {
              bits.push(levelRoman(presence.level));
            }
            if (presence.slowTimer > 0) bits.push("still");
            meta.textContent = bits.join(" · ");
          }
          updateLieArtEl(
            el,
            lieArtDefId(presence),
            presence.level,
            lieArtFallbackId(presence),
          );
          el.classList.toggle("type-confusion", presence.lieType === "confusion");
          el.classList.toggle("type-fear", presence.lieType === "fear");
          el.classList.toggle("type-despair", presence.lieType === "despair");
          el.classList.toggle("type-doubt", presence.lieType === "doubt");
          el.classList.toggle("type-dogma", presence.lieType === "dogma");
          el.classList.toggle("is-boss", isDogmaPresence(presence));
        } else if (meta && presence.side === "player") {
          const bits = [
            presence.category ?? "prayer",
            levelRoman(presence.level),
          ];
          if (presence.fortifyTimer > 0) bits.push("renewed");
          meta.textContent = bits.join(" · ");
          updatePrayerArtEl(el, presence.defId, presence.level);
        }
      });
    }

    this.applyVitals(vitals);
    this.markSettledLane(board);
    this.updateMotionIntents(board);
  }

  private bossPlateTimer = 0;

  private showBossPlate(
    event: Extract<BoardFxEvent, { type: "boss.arrive" | "boss.settle" }>,
  ): void {
    const plate = this.ensureBossPlate();
    const kicker = plate.querySelector(".settled-arrive-kicker");
    const claim = plate.querySelector(".settled-arrive-claim");
    if (kicker) {
      kicker.textContent =
        event.type === "boss.settle" ? "It sat down" : "Settled";
    }
    if (claim) claim.textContent = `“${event.claim}”`;
    plate.classList.toggle("is-settle", event.type === "boss.settle");
    plate.classList.remove("hidden");
    window.clearTimeout(this.bossPlateTimer);
    this.bossPlateTimer = window.setTimeout(() => {
      plate.classList.add("hidden");
    }, 1600);
  }

  private ensureBossPlate(): HTMLElement {
    let plate = this.root.querySelector<HTMLElement>(".settled-arrive");
    if (plate) return plate;
    plate = document.createElement("div");
    plate.className = "settled-arrive hidden";
    plate.setAttribute("role", "status");
    plate.setAttribute("aria-live", "polite");
    plate.innerHTML =
      `<p class="settled-arrive-kicker">Settled</p>` +
      `<p class="settled-arrive-claim"></p>`;
    this.root.appendChild(plate);
    return plate;
  }

  private markSettledLane(board: BoardState): void {
    let lane: number | null = null;
    for (const id of LAYER_ORDER) {
      board[id].slots.forEach((p, s) => {
        if (p && isDogmaPresence(p) && p.level > 0) lane = s;
      });
    }
    this.root.querySelectorAll(".slot").forEach((el) => {
      const slot = Number((el as HTMLElement).dataset.slot);
      const isLane = lane !== null && slot === lane;
      el.classList.toggle("settled-lane", isLane);
      const hasBoss = isLane && el.querySelector(".presence.is-boss");
      el.classList.toggle("has-boss", !!hasBoss);
    });
  }

  // --- Motion intents: advance arrows (A) + multiply forks (B) ------------

  private ensureMotionOverlay(): void {
    if (this.motionSvg && this.motionSvg.isConnected) return;

    const NS = "http://www.w3.org/2000/svg";
    const svg = document.createElementNS(NS, "svg");
    svg.classList.add("board-motion-overlay");
    svg.setAttribute("aria-hidden", "true");
    // viewBox set on each update from board client size
    svg.setAttribute("preserveAspectRatio", "none");

    const defs = document.createElementNS(NS, "defs");
    defs.append(
      this.makeArrowMarker(NS, "intent-head-lie", "#c4a8f0"),
      this.makeArrowMarker(NS, "intent-head-prayer", "#d4b46a"),
      this.makeArrowMarker(NS, "intent-head-clash", "#e0a88a"),
      this.makeArrowMarker(NS, "intent-head-multiply", "#e8a0c8"),
      this.makeArrowMarker(NS, "intent-head-tax", "#e8b07a"),
      this.makeArrowMarker(NS, "intent-head-dogma", "#e0a84a"),
    );
    const layer = document.createElementNS(NS, "g");
    layer.classList.add("board-motion-intents");
    svg.append(defs, layer);

    // Above layer bands (pointer-events: none so drag/inspect still work)
    this.root.prepend(svg);
    this.motionSvg = svg;
    this.motionLayer = layer;
  }

  private makeArrowMarker(
    NS: string,
    id: string,
    color: string,
  ): SVGMarkerElement {
    const marker = document.createElementNS(NS, "marker") as SVGMarkerElement;
    marker.setAttribute("id", id);
    marker.setAttribute("viewBox", "0 0 10 10");
    marker.setAttribute("refX", "8");
    marker.setAttribute("refY", "5");
    marker.setAttribute("markerWidth", "6");
    marker.setAttribute("markerHeight", "6");
    marker.setAttribute("orient", "auto-start-reverse");
    const path = document.createElementNS(NS, "path");
    path.setAttribute("d", "M 0 0 L 10 5 L 0 10 z");
    path.setAttribute("fill", color);
    marker.appendChild(path);
    return marker;
  }

  private clearMotionDestClasses(): void {
    this.root.querySelectorAll(".slot.advance-dest, .slot.multiply-dest").forEach((el) => {
      el.classList.remove(
        "advance-dest",
        "advance-dest-empty",
        "advance-dest-clash",
        "advance-dest-ally",
        "advance-dest-imminent",
        "advance-dest-lie",
        "advance-dest-prayer",
        "multiply-dest",
        "multiply-dest-empty",
        "multiply-dest-clash",
        "multiply-dest-primary",
        "multiply-dest-secondary",
        "multiply-dest-imminent",
      );
      const h = el as HTMLElement;
      h.style.removeProperty("--advance-progress");
      h.style.removeProperty("--multiply-progress");
    });
  }

  private slotEl(layer: LayerId, slot: number): HTMLElement | null {
    return this.root.querySelector(
      `.slot[data-layer="${layer}"][data-slot="${slot}"]`,
    ) as HTMLElement | null;
  }

  private presenceEl(instanceId: string): HTMLElement | null {
    return this.root.querySelector(
      `.presence[data-id="${CSS.escape(instanceId)}"]`,
    ) as HTMLElement | null;
  }

  /**
   * Apply combat feedback prefs (motion intensity). Call on load + panel change.
   */
  setCombatFeedback(settings: CombatFeedbackSettings): void {
    this.motionIntensity = effectiveMotion(settings);
    this.motion = motionFeatures(this.motionIntensity);
    this.root.classList.toggle(
      "motion-off",
      this.motionIntensity === "off",
    );
    this.root.classList.toggle(
      "motion-reduced",
      this.motionIntensity === "reduced",
    );
    this.root.classList.toggle(
      "motion-full",
      this.motionIntensity === "full",
    );
    if (!this.motion.actionFx) {
      this.liveFx = [];
      this.slotBurstQueue = [];
      this.applySlotBursts();
      this.motionLayer?.replaceChildren();
    }
  }

  /**
   * Queue a discrete combat VFX (from Game.onBoardFx).
   * Safe to call any time; drawn on next motion pass.
   */
  playFx(event: BoardFxEvent): void {
    if (event.type === "boss.arrive" || event.type === "boss.settle") {
      this.showBossPlate(event);
    }
    if (!this.motion.actionFx) return;
    if (this.motion.actionFxCriticalOnly) {
      const q = event.type === "strike" ? event.quality : undefined;
      if (!isCriticalBoardFx(event.type, q)) return;
    }

    const lifeMs =
      event.type === "strike"
        ? 480
        : event.type === "silence"
          ? 700
          : event.type === "redeem"
            ? 720
            : event.type === "degrade"
              ? 420
              : event.type === "still"
                ? 550
                : event.type === "strain"
                  ? 500
                  : event.type === "boss.arrive" || event.type === "boss.settle"
                    ? 1400
                    : 400;
    this.liveFx.push({
      id: ++this.fxSeq,
      event,
      born: performance.now(),
      lifeMs,
    });
    // Cap spam under multi-chip
    while (this.liveFx.length > 24) this.liveFx.shift();

    // Slot-local CSS bursts (survive full board rebuilds via queue re-apply)
    if (
      event.type === "silence" ||
      event.type === "redeem" ||
      event.type === "degrade" ||
      event.type === "still" ||
      event.type === "strain" ||
      event.type === "strike"
    ) {
      const cls =
        event.type === "strike"
          ? `fx-burst-strike fx-burst-strike-${event.quality}`
          : event.type === "silence"
            ? event.side === "enemy"
              ? "fx-burst-silence"
              : "fx-burst-break"
            : event.type === "redeem"
              ? "fx-burst-redeem"
              : event.type === "degrade"
                ? "fx-burst-degrade"
                : event.type === "still"
                  ? "fx-burst-still"
                  : "fx-burst-strain";
      this.slotBurstQueue.push({
        layer: event.layer,
        slot: event.slot,
        className: cls,
        until: performance.now() + lifeMs,
      });
      while (this.slotBurstQueue.length > 32) this.slotBurstQueue.shift();
      this.applySlotBursts();
    }
  }

  private applySlotBursts(): void {
    const now = performance.now();
    this.slotBurstQueue = this.slotBurstQueue.filter((b) => b.until > now);
    // Clear previous burst classes we own
    this.root.querySelectorAll(".slot[class*='fx-burst']").forEach((el) => {
      el.classList.forEach((c) => {
        if (c.startsWith("fx-burst")) el.classList.remove(c);
      });
    });
    for (const b of this.slotBurstQueue) {
      const slot = this.slotEl(b.layer, b.slot);
      if (!slot) continue;
      for (const c of b.className.split(/\s+/)) {
        if (c) slot.classList.add(c);
      }
    }
  }

  /**
   * Draw windup motion: advance arrows + multiply forks + contest + action FX.
   * Count is small (≤9 units × few candidates).
   */
  private updateMotionIntents(board: BoardState): void {
    this.ensureMotionOverlay();
    const svg = this.motionSvg;
    const layer = this.motionLayer;
    if (!svg || !layer) return;

    this.clearMotionDestClasses();
    this.applySlotBursts();

    const boardRect = this.root.getBoundingClientRect();
    const w = Math.max(1, boardRect.width);
    const h = Math.max(1, boardRect.height);
    svg.setAttribute("viewBox", `0 0 ${w} ${h}`);
    svg.setAttribute("width", String(w));
    svg.setAttribute("height", String(h));

    layer.replaceChildren();
    const NS = "http://www.w3.org/2000/svg";
    const feat = this.motion;

    if (feat.advanceArrows) {
      for (const intent of collectAdvanceIntents(board)) {
        this.applyAdvanceDestGhost(intent);
        this.drawAdvanceArrow(NS, layer, boardRect, intent);
      }
      const taxIntents = collectTaxIntents(board);
      this.applyTaxDest(taxIntents);
      for (const intent of taxIntents) {
        this.drawTaxArrow(NS, layer, boardRect, intent);
      }
    } else {
      this.applyTaxDest([]);
    }

    if (feat.multiplyForks) {
      for (const intent of collectMultiplyIntents(board)) {
        this.drawMultiplyForks(NS, layer, boardRect, intent);
      }
    }

    // Commit trail: freshly multiplied children keep anim-multiply briefly
    if (feat.multiplyTrails) {
      this.drawMultiplyCommitTrails(NS, layer, boardRect, board);
    }

    // Continuous contest grind (Phase C)
    if (feat.contestLinks) {
      this.drawContestLinks(NS, layer, boardRect, board);
    }

    // Discrete action strokes / bursts
    if (feat.actionFx) {
      this.drawLiveFx(NS, layer, boardRect);
    }
  }

  private drawContestLinks(
    NS: string,
    layer: SVGGElement,
    boardRect: DOMRect,
    board: BoardState,
  ): void {
    const links = collectContestLinks(board);
    // Dedupe undirected pairs so two-way contest is one line
    const seen = new Set<string>();
    for (const link of links) {
      const a = `${link.fromLayer}:${link.fromSlot}`;
      const b = `${link.toLayer}:${link.toSlot}`;
      const key = a < b ? `${a}|${b}` : `${b}|${a}`;
      if (seen.has(key)) continue;
      seen.add(key);

      const fromEl =
        this.presenceAt(link.fromLayer, link.fromSlot) ??
        this.slotEl(link.fromLayer, link.fromSlot);
      const toEl =
        this.presenceAt(link.toLayer, link.toSlot) ??
        this.slotEl(link.toLayer, link.toSlot);
      if (!fromEl || !toEl) continue;

      const fr = fromEl.getBoundingClientRect();
      const tr = toEl.getBoundingClientRect();
      const fromX = fr.left + fr.width / 2 - boardRect.left;
      const fromY = fr.top + fr.height / 2 - boardRect.top;
      const toX = tr.left + tr.width / 2 - boardRect.left;
      const toY = tr.top + tr.height / 2 - boardRect.top;
      if (Math.hypot(toX - fromX, toY - fromY) < 8) continue;

      const g = document.createElementNS(NS, "g") as SVGGElement;
      g.classList.add(
        "intent-arrow",
        "intent-contest",
        link.fromSide === "player" ? "contest-from-prayer" : "contest-from-lie",
      );
      g.style.setProperty("--intensity", String(link.intensity));

      const d = this.intentPath(fromX, fromY, toX, toY, 0.06);
      const glow = document.createElementNS(NS, "path") as SVGPathElement;
      glow.classList.add("intent-glow");
      glow.setAttribute("d", d);
      const shaft = document.createElementNS(NS, "path") as SVGPathElement;
      shaft.classList.add("intent-shaft");
      shaft.setAttribute("d", d);
      const op = 0.12 + link.intensity * 0.35;
      shaft.style.opacity = String(op);
      glow.style.opacity = String(op * 0.7);
      g.append(glow, shaft);
      layer.appendChild(g);
    }
  }

  private presenceAt(layer: LayerId, slot: number): HTMLElement | null {
    return this.root.querySelector(
      `.slot[data-layer="${layer}"][data-slot="${slot}"] .presence`,
    ) as HTMLElement | null;
  }

  private drawLiveFx(
    NS: string,
    layer: SVGGElement,
    boardRect: DOMRect,
  ): void {
    const now = performance.now();
    this.liveFx = this.liveFx.filter((fx) => now - fx.born < fx.lifeMs);

    for (const fx of this.liveFx) {
      const e = fx.event;
      const t = Math.max(0, Math.min(1, (now - fx.born) / fx.lifeMs));
      const dest = this.slotEl(e.layer, e.slot);
      if (!dest) continue;
      const dr = dest.getBoundingClientRect();
      const toX = dr.left + dr.width / 2 - boardRect.left;
      const toY = dr.top + dr.height / 2 - boardRect.top;

      if (e.type === "strike") {
        // Arc rises from hand side (board bottom) into the target
        const fromX = boardRect.width * (0.35 + e.slot * 0.15);
        const fromY = boardRect.height - 6;
        const g = this.makeIntentGroup(NS, {
          classes: [
            "intent-arrow",
            "intent-strike",
            `intent-strike-${e.quality}`,
            t > 0.7 ? "intent-strike-fade" : "",
          ].filter(Boolean),
          id: `fx-strike-${fx.id}`,
          progress: Math.min(1, t * 1.6),
          fromX,
          fromY,
          toX,
          toY,
          headId: this.strikeHeadId(e.quality),
          primary: true,
          curveScale: 0.2,
        });
        // Fade out late
        const fade = t > 0.55 ? 1 - (t - 0.55) / 0.45 : 1;
        g.style.opacity = String(Math.max(0, fade));
        layer.appendChild(g);

        // Impact ring
        this.appendImpactRing(NS, layer, toX, toY, e.quality, t);
      } else if (e.type === "silence" || e.type === "redeem") {
        this.appendImpactRing(
          NS,
          layer,
          toX,
          toY,
          e.type === "redeem" ? "meet" : "silence",
          t,
          e.type === "redeem" ? 28 : 34,
        );
      } else if (e.type === "degrade") {
        this.appendImpactRing(NS, layer, toX, toY, "weak", t, 18);
      } else if (e.type === "still") {
        this.appendImpactRing(NS, layer, toX, toY, "still", t, 26);
      } else if (e.type === "strain") {
        this.appendImpactRing(NS, layer, toX, toY, "strain", t, 22);
      }
    }
  }

  private strikeHeadId(q: StrikeQuality): string {
    if (q === "meet") return "intent-head-prayer";
    if (q === "miss") return "intent-head-clash";
    return "intent-head-lie";
  }

  private appendImpactRing(
    NS: string,
    layer: SVGGElement,
    x: number,
    y: number,
    kind: StrikeQuality | "silence" | "still" | "strain",
    t: number,
    maxR = 30,
  ): void {
    const circle = document.createElementNS(NS, "circle") as SVGCircleElement;
    circle.classList.add("fx-impact-ring", `fx-impact-${kind}`);
    const r = 4 + t * maxR;
    circle.setAttribute("cx", x.toFixed(1));
    circle.setAttribute("cy", y.toFixed(1));
    circle.setAttribute("r", r.toFixed(1));
    circle.style.opacity = String(Math.max(0, 0.75 * (1 - t)));
    layer.appendChild(circle);
  }

  private drawAdvanceArrow(
    NS: string,
    layer: SVGGElement,
    boardRect: DOMRect,
    intent: AdvanceIntent,
  ): void {
    const src = this.presenceEl(intent.presenceId);
    const dest = this.slotEl(intent.toLayer, intent.toSlot);
    if (!src || !dest) return;

    const sr = src.getBoundingClientRect();
    const dr = dest.getBoundingClientRect();

    // Lies push down (inward); prayers push up (outward)
    const fromX = sr.left + sr.width / 2 - boardRect.left;
    const toX = dr.left + dr.width / 2 - boardRect.left;
    let fromY: number;
    let toY: number;
    if (intent.side === "enemy") {
      fromY = sr.bottom - boardRect.top - 2;
      toY = dr.top - boardRect.top + 10;
    } else {
      fromY = sr.top - boardRect.top + 2;
      toY = dr.bottom - boardRect.top - 10;
    }

    if (Math.hypot(toX - fromX, toY - fromY) < 12) return;

    const srcPresence = this.root.querySelector(
      `.presence[data-id="${intent.presenceId}"]`,
    );
    const isBoss = srcPresence?.classList.contains("is-boss");
    const headId =
      intent.kind === "clash"
        ? "intent-head-clash"
        : isBoss
          ? "intent-head-dogma"
          : intent.side === "enemy"
            ? "intent-head-lie"
            : "intent-head-prayer";

    const g = this.makeIntentGroup(NS, {
      classes: [
        "intent-arrow",
        intent.side === "enemy" ? "intent-lie" : "intent-prayer",
        `intent-kind-${intent.kind}`,
        ...(isBoss ? ["intent-boss"] : []),
        ...(this.motion.animate && intent.progress >= ADVANCE_INTENT_IMMINENT
          ? ["intent-imminent"]
          : []),
      ],
      id: intent.presenceId,
      progress: intent.progress,
      fromX,
      fromY,
      toX,
      toY,
      headId,
      primary: true,
    });
    layer.appendChild(g);
  }

  private secretPlaceEl(): HTMLElement | null {
    const arena = this.root.closest(".arena");
    return (
      (arena?.querySelector(".secret-place-core") as HTMLElement | null) ??
      (arena?.querySelector(".secret-place-strip") as HTMLElement | null)
    );
  }

  private applyTaxDest(intents: TaxIntent[]): void {
    const strip =
      (this.root.closest(".arena")?.querySelector(
        ".secret-place-strip",
      ) as HTMLElement | null) ?? null;
    if (!strip) return;
    const any = intents.length > 0;
    const imminent = intents.some((i) => i.progress >= TAX_INTENT_IMMINENT);
    strip.classList.toggle("tax-dest", any);
    strip.classList.toggle("tax-dest-imminent", imminent);
    const best = intents.reduce((m, i) => Math.max(m, i.progress), 0);
    strip.style.setProperty("--tax-progress", String(best));
  }

  private drawTaxArrow(
    NS: string,
    layer: SVGGElement,
    boardRect: DOMRect,
    intent: TaxIntent,
  ): void {
    const src = this.presenceEl(intent.presenceId);
    const dest = this.secretPlaceEl();
    if (!src || !dest) return;

    const sr = src.getBoundingClientRect();
    const dr = dest.getBoundingClientRect();
    const fromX = sr.left + sr.width / 2 - boardRect.left;
    const fromY = sr.bottom - boardRect.top - 2;
    const toX = dr.left + dr.width / 2 - boardRect.left;
    const toY = dr.top - boardRect.top + 8;
    if (Math.hypot(toX - fromX, toY - fromY) < 12) return;

    const g = this.makeIntentGroup(NS, {
      classes: [
        "intent-arrow",
        "intent-tax",
        ...(this.motion.animate && intent.progress >= TAX_INTENT_IMMINENT
          ? ["intent-imminent"]
          : []),
      ],
      id: `tax-${intent.presenceId}`,
      progress: intent.progress,
      fromX,
      fromY,
      toX,
      toY,
      headId: "intent-head-tax",
      primary: true,
      curveScale: 0.04,
    });
    layer.appendChild(g);
  }

  private drawMultiplyForks(
    NS: string,
    layer: SVGGElement,
    boardRect: DOMRect,
    intent: MultiplyIntent,
  ): void {
    const src = this.presenceEl(intent.presenceId);
    if (!src) return;
    const sr = src.getBoundingClientRect();
    const fromX = sr.left + sr.width / 2 - boardRect.left;
    const fromY = sr.top + sr.height / 2 - boardRect.top;

    for (const cand of intent.candidates) {
      this.applyMultiplyDestGhost(intent, cand.layer, cand.slot, cand.kind, cand.primary);

      const dest = this.slotEl(cand.layer, cand.slot);
      if (!dest) continue;
      const dr = dest.getBoundingClientRect();
      const toX = dr.left + dr.width / 2 - boardRect.left;
      const toY = dr.top + dr.height / 2 - boardRect.top;
      if (Math.hypot(toX - fromX, toY - fromY) < 10) continue;

      const sameLayer = cand.layer === intent.fromLayer;
      const g = this.makeIntentGroup(NS, {
        classes: [
          "intent-arrow",
          "intent-multiply",
          `intent-kind-${cand.kind}`,
          cand.primary ? "intent-primary" : "intent-secondary",
          sameLayer ? "intent-sideways" : "intent-inward",
          ...(this.motion.animate &&
          intent.progress >= MULTIPLY_INTENT_IMMINENT
            ? ["intent-imminent"]
            : []),
        ],
        id: `${intent.presenceId}->${cand.layer}:${cand.slot}`,
        progress: intent.progress,
        fromX,
        fromY,
        toX,
        toY,
        headId:
          cand.kind === "clash" ? "intent-head-clash" : "intent-head-multiply",
        primary: cand.primary,
        // Sideways forks: slightly more curve so they read as split, not advance
        curveScale: sameLayer ? 0.18 : 0.1,
      });
      layer.appendChild(g);
    }
  }

  /**
   * Brief parent→child trail while a spawnling still has anim-multiply.
   * Parent guessed by same lineage on adjacent / outward cell.
   */
  private drawMultiplyCommitTrails(
    NS: string,
    layer: SVGGElement,
    boardRect: DOMRect,
    board: BoardState,
  ): void {
    for (const id of LAYER_ORDER) {
      board[id].slots.forEach((child, slot) => {
        if (!child || child.side !== "enemy") return;
        if (child.anim !== "multiply") return;

        const parent = this.findMultiplyParent(board, id, slot, child);
        if (!parent) return;

        const src = this.presenceEl(parent.presence.instanceId);
        const dest = this.presenceEl(child.instanceId);
        if (!src || !dest) return;

        const sr = src.getBoundingClientRect();
        const dr = dest.getBoundingClientRect();
        const fromX = sr.left + sr.width / 2 - boardRect.left;
        const fromY = sr.top + sr.height / 2 - boardRect.top;
        const toX = dr.left + dr.width / 2 - boardRect.left;
        const toY = dr.top + dr.height / 2 - boardRect.top;

        const g = this.makeIntentGroup(NS, {
          classes: [
            "intent-arrow",
            "intent-multiply",
            "intent-commit-trail",
          ],
          id: `trail-${child.instanceId}`,
          progress: 1,
          fromX,
          fromY,
          toX,
          toY,
          headId: "intent-head-multiply",
          primary: true,
          curveScale: 0.12,
        });
        layer.appendChild(g);
      });
    }
  }

  private findMultiplyParent(
    board: BoardState,
    childLayer: LayerId,
    childSlot: number,
    child: Presence,
  ): { presence: Presence; layer: LayerId; slot: number } | null {
    // Same layer neighbors first (most common: multiplies beside)
    for (const s of [childSlot - 1, childSlot + 1, 0, 1, 2]) {
      if (s < 0 || s > 2 || s === childSlot) continue;
      const p = board[childLayer].slots[s];
      if (
        p &&
        p.side === "enemy" &&
        p.defId === child.defId &&
        p.spreadGen < child.spreadGen
      ) {
        return { presence: p, layer: childLayer, slot: s };
      }
    }
    // Inward spawn: parent on the outward layer, same home lane
    const outwardIdx = LAYER_ORDER.indexOf(childLayer) - 1;
    if (outwardIdx >= 0) {
      const outL = LAYER_ORDER[outwardIdx]!;
      for (let s = 0; s < 3; s++) {
        const p = board[outL].slots[s];
        if (
          p &&
          p.side === "enemy" &&
          p.defId === child.defId &&
          p.spreadGen < child.spreadGen
        ) {
          return { presence: p, layer: outL, slot: s };
        }
      }
    }
    return null;
  }

  private makeIntentGroup(
    NS: string,
    opts: {
      classes: string[];
      id: string;
      progress: number;
      fromX: number;
      fromY: number;
      toX: number;
      toY: number;
      headId: string;
      primary: boolean;
      curveScale?: number;
    },
  ): SVGGElement {
    const g = document.createElementNS(NS, "g") as SVGGElement;
    g.classList.add(...opts.classes);
    g.dataset.id = opts.id;
    g.style.setProperty("--progress", String(opts.progress));

    const d = this.intentPath(
      opts.fromX,
      opts.fromY,
      opts.toX,
      opts.toY,
      opts.curveScale ?? 0.08,
    );

    const glow = document.createElementNS(NS, "path") as SVGPathElement;
    glow.classList.add("intent-glow");
    glow.setAttribute("d", d);

    const shaft = document.createElementNS(NS, "path") as SVGPathElement;
    shaft.classList.add("intent-shaft");
    shaft.setAttribute("d", d);
    shaft.setAttribute("marker-end", `url(#${opts.headId})`);

    const len = Math.hypot(opts.toX - opts.fromX, opts.toY - opts.fromY);
    // Secondary multiply forks: shorter / fainter draw so primary reads as the pick
    const prog = opts.primary
      ? opts.progress
      : Math.min(opts.progress, opts.progress * 0.55 + 0.12);
    const draw = Math.max(8, len * prog);
    shaft.style.strokeDasharray = `${draw} ${len + 40}`;
    glow.style.strokeDasharray = `${draw} ${len + 40}`;
    const baseOp = opts.primary ? 0.4 : 0.22;
    const baseGlow = opts.primary ? 0.15 : 0.08;
    shaft.style.opacity = String(baseOp + prog * 0.55);
    glow.style.opacity = String(baseGlow + prog * 0.35);

    g.append(glow, shaft);
    return g;
  }

  private intentPath(
    x1: number,
    y1: number,
    x2: number,
    y2: number,
    curveScale = 0.08,
  ): string {
    // Slight curve so multi-lane arrows don’t stack into one line
    const mx = (x1 + x2) / 2;
    const my = (y1 + y2) / 2;
    const dx = x2 - x1;
    const dy = y2 - y1;
    const len = Math.hypot(dx, dy) || 1;
    const bump = Math.min(18, len * curveScale);
    const nx = (-dy / len) * bump;
    const ny = (dx / len) * bump;
    const cx = mx + nx;
    const cy = my + ny;
    return `M ${x1.toFixed(1)} ${y1.toFixed(1)} Q ${cx.toFixed(1)} ${cy.toFixed(1)} ${x2.toFixed(1)} ${y2.toFixed(1)}`;
  }

  private applyAdvanceDestGhost(intent: AdvanceIntent): void {
    const slot = this.slotEl(intent.toLayer, intent.toSlot);
    if (!slot) return;
    slot.classList.add(
      "advance-dest",
      `advance-dest-${intent.kind}`,
      intent.side === "enemy" ? "advance-dest-lie" : "advance-dest-prayer",
    );
    if (this.motion.animate && intent.progress >= ADVANCE_INTENT_IMMINENT) {
      slot.classList.add("advance-dest-imminent");
    }
    slot.style.setProperty(
      "--advance-progress",
      String(intent.progress),
    );
  }

  private applyMultiplyDestGhost(
    intent: MultiplyIntent,
    layer: LayerId,
    slotIndex: number,
    kind: "empty" | "clash",
    primary: boolean,
  ): void {
    const slot = this.slotEl(layer, slotIndex);
    if (!slot) return;
    // Advance ghost wins if both mark the same cell (rarer)
    if (slot.classList.contains("advance-dest")) return;
    slot.classList.add(
      "multiply-dest",
      `multiply-dest-${kind}`,
      primary ? "multiply-dest-primary" : "multiply-dest-secondary",
    );
    if (
      this.motion.animate &&
      intent.progress >= MULTIPLY_INTENT_IMMINENT &&
      primary
    ) {
      slot.classList.add("multiply-dest-imminent");
    }
    slot.style.setProperty("--multiply-progress", String(intent.progress));
  }
}
